/**
 * Error Handler Utility for API Responses
 * Handles inconsistent API response structures and provides consistent error handling
 */

class ErrorHandler {
  /**
   * Parse API response to extract meaningful success/error information
   * @param {Object} response - API response object
   * @returns {Object} Parsed response with consistent structure
   */
  static parseResponse(response) {
    if (!response) {
      return {
        success: false,
        error: 'No response received from server',
        data: null
      };
    }

    // Handle different API response structures
    if (response.success !== undefined) {
      // Direct success field
      return {
        success: response.success,
        error: response.error || response.info || null,
        data: response.data || response
      };
    } else if (response.data && response.data.success !== undefined) {
      // Nested success field
      return {
        success: response.data.success,
        error: response.data.error || response.data.info || null,
        data: response.data.data || response.data
      };
    } else if (response.error || response.info) {
      // Error/info field present
      return {
        success: false,
        error: response.error || response.info,
        data: response.data || null
      };
    } else {
      // Assume success if no error indicators
      return {
        success: true,
        error: null,
        data: response.data || response
      };
    }
  }

  /**
   * Check if error is a permission/role related error
   * @param {Error} error - Error object from API call
   * @returns {Object|null} Permission error details or null
   */
  static isPermissionError(error) {
    if (!error?.response) return null;
    
    const { status, data } = error.response;
    
    if (status === 403) {
      // Check for various permission error formats
      if (data?.error === 'Insufficient sub-role permissions' || 
          data?.category && data?.permission ||
          data?.code === 'PERMISSION_DENIED' ||
          (data?.error && data?.error.toLowerCase().includes('permission'))) {
        return {
          isPermissionError: true,
          category: data.category || 'general',
          permission: data.permission || 'unknown',
          message: data.details || data.error || 'Permission denied',
          rawError: data
        };
      }
    }
    
    return null;
  }

  /**
   * Handle API errors consistently
   * @param {Error} error - Error object from API call
   * @returns {Object} Standardized error object
   */
  static handleError(error) {
    console.error('API Error:', error);

    // Check for permission errors first
    const permissionError = this.isPermissionError(error);
    if (permissionError) {
      return {
        success: false,
        error: permissionError.message,
        isPermissionError: true,
        permission: permissionError.permission,
        category: permissionError.category,
        data: null
      };
    }

    // Extract error message from different error structures
    let errorMessage = 'An unexpected error occurred';
    
    if (error.response) {
      // Server responded with error status
      const responseData = error.response.data;
      errorMessage = responseData?.error || 
                    responseData?.info || 
                    responseData?.message || 
                    `Server error: ${error.response.status}`;
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = 'No response received from server';
    } else {
      // Something else happened
      errorMessage = error.message || 'Unknown error occurred';
    }

    return {
      success: false,
      error: errorMessage,
      data: null
    };
  }

  /**
   * Validate required parameters
   * @param {Object} params - Parameters to validate
   * @param {Array} required - Array of required parameter names
   * @returns {Object} Validation result
   */
  static validateParams(params, required) {
    const missing = required.filter(param => 
      params[param] === undefined || params[param] === null || params[param] === ''
    );

    if (missing.length > 0) {
      return {
        valid: false,
        error: `Missing required parameters: ${missing.join(', ')}`
      };
    }

    return {
      valid: true,
      error: null
    };
  }

  /**
   * Create a standardized success response
   * @param {any} data - Response data
   * @param {string} message - Success message
   * @returns {Object} Standardized success response
   */
  static createSuccessResponse(data = null, message = 'Operation completed successfully') {
    return {
      success: true,
      data,
      message
    };
  }

  /**
   * Create a standardized error response
   * @param {string} error - Error message
   * @param {any} data - Additional error data
   * @returns {Object} Standardized error response
   */
  static createErrorResponse(error, data = null) {
    return {
      success: false,
      error,
      data
    };
  }
}

export default ErrorHandler;